import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';

export const fontOptions = [
  { name: 'Moderne', value: 'Lexend, sans-serif' },
  { name: 'Classique', value: '"Bodoni Moda", serif' },
  { name: 'Relax', value: '"Edu SA Beginner", cursive' },
  { name: 'Comic', value: '"Comic Neue", cursive' },
] as const;

export type FontFamily = typeof fontOptions[number]['name'];

interface TypographyContextType {
  fontFamily: FontFamily;
  setFontFamily: (font: FontFamily) => void;
}

const TypographyContext = createContext<TypographyContextType | undefined>(undefined);

export function TypographyProvider({ children }: { children: ReactNode }) {
  const [fontFamily, setFontFamilyState] = useState<FontFamily>('Moderne');

  useEffect(() => {
    // Charger la préférence depuis localStorage
    const saved = localStorage.getItem('app-font-family');
    if (saved && (saved === 'Moderne' || saved === 'Classique' || saved === 'Relax' || saved === 'Comic')) {
      setFontFamilyState(saved as FontFamily);
    }
  }, []);

  useEffect(() => {
    // Appliquer la font à la variable CSS
    const fontConfig = fontOptions.find(f => f.name === fontFamily);
    if (fontConfig) {
      document.documentElement.style.setProperty('--font-family', fontConfig.value);
    }
  }, [fontFamily]);

  const setFontFamily = useCallback((font: FontFamily) => {
    setFontFamilyState(font);
    localStorage.setItem('app-font-family', font);
  }, []);

  const value = useMemo(() => ({ fontFamily, setFontFamily }), [fontFamily, setFontFamily]);

  return (
    <TypographyContext.Provider value={value}>
      {children}
    </TypographyContext.Provider>
  );
}

export function useTypography() {
  const context = useContext(TypographyContext);
  if (context === undefined) {
    throw new Error('useTypography must be used within a TypographyProvider');
  }
  return context;
}